import React, { createContext, useContext, useState, useEffect } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { useLocation } from 'react-router-dom';
import { debugCustomFont } from '../utils/fontDebugger';

const FontContext = createContext();

export const useFont = () => {
    return useContext(FontContext);
};

export const FontProvider = ({ children }) => {
    const [allFonts, setAllFonts] = useState({
        selectedFont: 'Arial', // Standardwert
        globalSmallFont: 'Arial', // Standardwert
        globalBigFont: 'Arial', // Standardwert
        customFonts: [], // Custom uploaded fonts
        loadedCustomFonts: new Set() // Track loaded custom fonts
    });

    const location = useLocation();
    const db = getDatabase();
    const auth = getAuth();

    // Dynamic custom font loading function (supports multi-weight)
    const loadCustomFont = async (fontData) => {
        const { name, weights, fileUrl, format = 'truetype', id } = fontData;
        
        // Check if font is already loaded
        if (allFonts.loadedCustomFonts.has(name)) {
            console.log(`Font ${name} already loaded - updating array if needed`);
            
            // ✅ FIX: Even if CSS is loaded, ensure font is in customFonts array
            setAllFonts(prev => {
                const fontExists = prev.customFonts.some(f => f.name === name);
                if (fontExists) {
                    return prev; // Already in array, no update needed
                }
                return {
                    ...prev,
                    customFonts: [...prev.customFonts, fontData]
                };
            });
            return;
        }
        
        try {
            let fontCSS = '';
            
            // Handle multi-weight font family
            if (weights && Array.isArray(weights)) {
                // console.log(`📤 Loading font family ${name} with ${weights.length} weights:`, weights);
                
                for (const weight of weights) {
                    // console.log(`  → Adding weight: ${weight.weight} (${weight.weightValue}) from ${weight.fileUrl}`);
                    fontCSS += `
                        @font-face {
                            font-family: '${name}';
                            src: url('${weight.fileUrl}') format('${weight.format}');
                            font-weight: ${weight.weightValue};
                            font-style: normal;
                            font-display: block;
                        }
                    `;
                }
                
                // console.log(`📝 Generated CSS for ${name}:`, fontCSS);
            } else {
                // Handle single weight font (legacy)
                fontCSS = `
                    @font-face {
                        font-family: '${name}';
                        src: url('${fileUrl}') format('${format}');
                        font-weight: normal;
                        font-style: normal;
                        font-display: block;
                    }
                `;
            }
            
            // ✅ CRITICAL: Check if already injected (prevents duplicates from customFontLoader.js)
            const existingStyle = document.querySelector(`[data-custom-font="${name}"]`);
            if (existingStyle) {
                console.log(`Font ${name} CSS already injected - skipping to prevent flicker`);
                return;
            }
            
            // Inject CSS
            const styleElement = document.createElement('style');
            styleElement.setAttribute('data-custom-font', name);
            styleElement.textContent = fontCSS;
            document.head.appendChild(styleElement);
            
            // console.log(`💉 CSS injected for ${name}. Total style elements:`, document.querySelectorAll('[data-custom-font]').length);
            
            // Debug: Check if font is available after injection
            // setTimeout(() => {
            //     const available = document.fonts.check(`16px "${name}"`);
            //     console.log(`🔍 Font availability check for "${name}": ${available ? '✅ Available' : '❌ Not available'}`);
            //     
            //     // List all available fonts
            //     const allFonts = Array.from(document.fonts).map(f => f.family);
            //     console.log(`📋 All loaded fonts:`, [...new Set(allFonts)]);
            // }, 1000);
            
            // ✅ IMPROVED: Robusteres Font-Loading mit Timeout
            try {
                await Promise.race([
                    document.fonts.load(`16px "${name}"`),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('Font loading timeout')), 5000))
                ]);
                
                // ✅ FIX: Update both loadedCustomFonts AND customFonts array
                setAllFonts(prev => {
                    // Check if font is already in customFonts array
                    const fontExists = prev.customFonts.some(f => f.name === name);
                    
                    return {
                        ...prev,
                        loadedCustomFonts: new Set([...prev.loadedCustomFonts, name]),
                        // Add font to customFonts array if not already there (for immediate UI update)
                        customFonts: fontExists ? prev.customFonts : [...prev.customFonts, fontData]
                    };
                });
                
                console.log(`✅ Custom font family loaded: ${name} (${weights?.length || 1} weights)`);
                
                // ✅ DEBUG: Disabled by default to reduce console noise
                // Uncomment to debug font loading issues:
                // setTimeout(() => debugCustomFont(name), 1000);
            } catch (fontLoadError) {
                // ✅ GRACEFUL DEGRADATION: Font-Loading fehlgeschlagen, aber CSS ist injiziert
                console.warn(`⚠️ Font loading failed for ${name}, but CSS injected (CORS issue?):`, fontLoadError.message);
                
                // ✅ FIX: Also update customFonts array in error case
                setAllFonts(prev => {
                    const fontExists = prev.customFonts.some(f => f.name === name);
                    
                    return {
                        ...prev,
                        loadedCustomFonts: new Set([...prev.loadedCustomFonts, name]),
                        customFonts: fontExists ? prev.customFonts : [...prev.customFonts, fontData]
                    };
                });
            }
            
        } catch (error) {
            console.error(`❌ Failed to load custom font ${name}:`, error);
        }
    };

    // Load custom fonts for current user
    useEffect(() => {
        const loadUserCustomFonts = async () => {
            const currentUser = auth.currentUser;
            if (!currentUser) {
                return;
            }
            
            try {
                const customFontsRef = ref(db, `/users/${currentUser.uid}/customFonts`);
                const snapshot = await get(customFontsRef);
                
                if (snapshot.exists()) {
                    const customFontsData = snapshot.val();
                    const customFontsArray = Object.entries(customFontsData).map(([key, value]) => ({
                        id: key,
                        ...value
                    }));
                    
                    // ✅ PARALLEL Loading: Alle Fonts gleichzeitig laden (kein Flackern)
                    await Promise.all(
                        customFontsArray.map(font => loadCustomFont(font))
                    );
                    
                    setAllFonts(prev => ({
                        ...prev,
                        customFonts: customFontsArray
                    }));
                }
            } catch (error) {
                console.error('Error loading custom fonts:', error);
            }
        };
        
        loadUserCustomFonts();
    }, [auth.currentUser]);

    useEffect(() => {
        const pathSegments = location.pathname.split('/');
        const isCustomUrl = pathSegments[1] === 'to';
        const isSettingsPage = pathSegments[1] === 'settings' && pathSegments[2] === 'page';
        const isDemoUrl = pathSegments[1] === 'demo';
        const isSettingsDemoPage = pathSegments[1] === 'settings' && pathSegments[2] === 'demoPage';
        const customUrl = isCustomUrl || isDemoUrl ? pathSegments[2] : null;
        const pageId = isSettingsPage || isSettingsDemoPage ? pathSegments[pathSegments.length - 1] : null;

        if ((isCustomUrl || isDemoUrl) && customUrl) {
            const refPath = isDemoUrl ? `/demoPublicPages/${customUrl}` : `/publicPages/${customUrl}`;
            get(ref(db, refPath)).then((snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    if (data.pageId && data.userId) {
                        loadFonts(db, data.pageId, data.userId, isDemoUrl);
                    } else {
                        loadFonts(db, pageId, auth.currentUser.uid, false);
                    }
                }
            });
        } else if ((isSettingsPage || isSettingsDemoPage) && pageId) {
            const userId = auth.currentUser ? auth.currentUser.uid : null;
            if (userId) {
                loadFonts(db, pageId, userId, isSettingsDemoPage);
            }
        }
    }, [location.pathname]);

    const loadFonts = (db, pageId, userId, isDemo) => {
        let basePath = isDemo ? `/demoPages/${userId}/${pageId}` : `/pages/${pageId}`;
        const fontRef = ref(db, `${basePath}/selectedFont`);
        const globalSmallFontRef = ref(db, `${basePath}/globalSmallFont`);
        const globalBigFontRef = ref(db, `${basePath}/globalBigFont`);

        const updateFont = (snapshot, fontKey) => {
            if (snapshot.exists()) {
                setAllFonts(prevFonts => ({
                    ...prevFonts,
                    [fontKey]: snapshot.val(),
                }));
            }
        };

        get(fontRef).then(snapshot => updateFont(snapshot, 'selectedFont'));
        get(globalSmallFontRef).then(snapshot => updateFont(snapshot, 'globalSmallFont'));
        get(globalBigFontRef).then(snapshot => updateFont(snapshot, 'globalBigFont'));
    };

    // Refresh custom fonts function
    const refreshCustomFonts = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        
        try {
            const customFontsRef = ref(db, `/users/${currentUser.uid}/customFonts`);
            const snapshot = await get(customFontsRef);
            
            if (snapshot.exists()) {
                const customFontsData = snapshot.val();
                const customFontsArray = Object.entries(customFontsData).map(([key, value]) => ({
                    id: key,
                    ...value
                }));
                
                // ✅ FIX: Remove duplicates by name (keep latest version)
                const uniqueFonts = customFontsArray.reduce((acc, font) => {
                    const existingIndex = acc.findIndex(f => f.name === font.name);
                    if (existingIndex >= 0) {
                        // Replace with newer version (based on uploadedAt)
                        const existing = acc[existingIndex];
                        if (font.uploadedAt > existing.uploadedAt) {
                            acc[existingIndex] = font;
                        }
                    } else {
                        acc.push(font);
                    }
                    return acc;
                }, []);
                
                // ✅ PARALLEL Loading: Nur neue Fonts laden, aber parallel
                await Promise.all(
                    uniqueFonts
                        .filter(font => !allFonts.loadedCustomFonts.has(font.name))
                        .map(font => loadCustomFont(font))
                );
                
                setAllFonts(prev => ({
                    ...prev,
                    customFonts: uniqueFonts
                }));
                
                console.log(`🔄 Refreshed custom fonts: ${uniqueFonts.length} unique fonts`);
                return uniqueFonts;
            }
        } catch (error) {
            console.error('Error refreshing custom fonts:', error);
        }
        
        return [];
    };

    return (
        <FontContext.Provider value={{ 
            ...allFonts, 
            refreshCustomFonts,
            loadCustomFont
        }}>
            {children}
        </FontContext.Provider>
    );
};
